import { Pagination } from "~/types/pagination";
import {
  getRequest,
  putRequest,
  postRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";

type Stats = {
  total: number;
  called: number;
  picked_up: number;
  not_picked_up: number;
  remaining: number;
};

const emptyStats = (): Stats => ({
  total: 0,
  called: 0,
  picked_up: 0,
  not_picked_up: 0,
  remaining: 0,
});

export const useAutoDialerStore = definePiniaStore("autoDialerStore", {
  state: () => ({
    aggregate: emptyStats() as Stats,
    categories: [] as Array<any>,
    products: [] as Array<any>,
    currentCategoryId: null as number | null,
    currentProductId: null as number | null,
    _listingTableData: [] as Array<any>,
    _pagination: {} as Pagination,
    _loading: false,
    showEditModal: false,
    listItemForEdit: {} as any,
    uploadModalVisible: false,
  }),

  actions: {
    buildQuery(params: Record<string, any>): string {
      const q = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === "") continue;
        if (Array.isArray(value)) {
          value.forEach((item) => q.append(`${key}[]`, String(item)));
        } else {
          q.append(key, String(value));
        }
      }
      return q.toString();
    },

    async getCategorySummary(params: Record<string, any> = {}) {
      this._loading = true;
      const qs = this.buildQuery(params);
      const { response, error } = await getRequest<any>(
        `tele-sales/auto-dialers/categories/summary?${qs}`,
      );
      if (!error) {
        this.aggregate = response.data.aggregate;
        this.categories = response.data.categories;
      }
      this._loading = false;
    },

    async getCategoryProducts(
      categoryId: number,
      params: Record<string, any> = {},
    ) {
      const qs = this.buildQuery(params);
      const { response, error } = await getRequest<any>(
        `tele-sales/auto-dialers/categories/${categoryId}/products?${qs}`,
      );
      if (!error) {
        this.products = response.data.products;
      }
      return this.products;
    },

    async getItems(
      categoryId: number,
      productId: number,
      params: Record<string, any> = {},
    ) {
      this._loading = true;
      const qs = this.buildQuery({
        category_id: categoryId,
        product_id: productId,
        length: 1500,
        ...params,
      });
      const { response, error } = await getRequest<
        TableDataWithPagination<any>
      >(`tele-sales/auto-dialers?${qs}`);
      if (!error) {
        const { data, ...pagination } = response.data;
        this._listingTableData = data;
        this._pagination = pagination as Pagination;
      }
      this._loading = false;
      return this._listingTableData;
    },

    async bulkUpload(formData: FormData) {
      return await postRequest<any>(
        "tele-sales/auto-dialers/bulk-upload",
        formData,
        true,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },

    async updateListItem(data: any) {
      const { response, error } = await putRequest<any>(
        `tele-sales/auto-dialers/${data.id}`,
        data,
      );

      if (!error && response?.data) {
        const index = this._listingTableData.findIndex(
          (item) => item.id === data.id,
        );
        if (index !== -1) {
          this._listingTableData[index] = response.data;
        }
        return response.data;
      }

      return null;
    },

    // Reuses the existing call-history update endpoint to sync a remark onto
    // the item's last call history.
    async updateCallHistoryRemarks(callHistoryId: number, remarks: string) {
      return await putRequest<any>(`cc/agent-call-histories/${callHistoryId}`, {
        remarks,
      });
    },

    async startBatch(payload: {
      category_id: number;
      product_id: number;
      from: number;
      length: number;
    }) {
      return await putRequest<any>("tele-sales/auto-dialers/start", payload);
    },

    async exportTable(params: Record<string, any> = {}) {
      const qs = this.buildQuery(params);
      const fileName = `auto_dial_items_${new Date().toISOString()}.xlsx`;
      await downloadWithSavePicker(
        `tele-sales/auto-dialers/export?${qs}`,
        fileName,
      );
    },

    setUploadModalVisible(visible: boolean) {
      this.uploadModalVisible = visible;
    },

    setListItemForEdit(item: any) {
      this.listItemForEdit = item;
    },
  },

  getters: {},
});
